import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Eye, Clock, TrendingUp, Search, Target, Timer, Play, BarChart3, PieChart as PieIcon, Zap, Calendar, GitBranch } from 'lucide-react';
import { Card, StatCard } from '../components/ui/Card';
import { Select } from '../components/ui/Select';
import { AreaChart } from '../components/charts/AreaChart';
import { PieChart } from '../components/charts/PieChart';
import { BarChart } from '../components/charts/BarChart';
import { GroupedBarChart } from '../components/charts/GroupedBarChart';
import { BubbleHeatmapChart } from '../components/charts/BubbleHeatmapChart';
import { analyticsApi, camerasApi, branchesApi } from '../services/api';
import { useChartColors } from '../hooks/useChartColors';
import { useTimezone } from '../hooks/useTimezone';

const periodOptions = [
  { value: '1h', label: 'Last hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '12h', label: 'Last 12 hours' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

// Get appropriate interval for timeseries based on period
const getTimeseriesInterval = (period: string) => {
  switch (period) {
    case '1h': return 5;   // 5 minute intervals
    case '6h': return 15;  // 15 minute intervals
    case '12h': return 30; // 30 minute intervals
    case 'today': return 15;
    default: return 60;    // hourly for longer periods
  }
};

export default function Dashboard() {
  const [period, setPeriod] = useState('today');
  const [selectedCameraId, setSelectedCameraId] = useState<string>('all');
  const [selectedBranchId, setSelectedBranchId] = useState<string>('all');
  const colors = useChartColors();
  const timezone = useTimezone();

  const { data: cameras } = useQuery({
    queryKey: ['cameras'],
    queryFn: () => camerasApi.list(),
    refetchInterval: 10000,
  });

  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: () => branchesApi.list(),
  });

  // Filter cameras by branch first, then by specific camera selection
  const branchFilteredCameras = selectedBranchId === 'all'
    ? cameras
    : selectedBranchId === 'unassigned'
      ? cameras?.filter((c) => !c.branch_id)
      : cameras?.filter((c) => c.branch_id === selectedBranchId);

  // Filter camera IDs based on selection
  const cameraIds = selectedCameraId === 'all'
    ? branchFilteredCameras?.map((c) => c.id) || []
    : [selectedCameraId];

  const { data: summary } = useQuery({
    queryKey: ['analytics', 'summary', cameraIds, period],
    queryFn: () => analyticsApi.getSummary(cameraIds, period),
    enabled: cameraIds.length > 0,
    refetchInterval: 30000,
  });

  const { data: timeseries } = useQuery({
    queryKey: ['analytics', 'timeseries', cameraIds, period],
    queryFn: () => analyticsApi.getTimeseries(cameraIds, period, getTimeseriesInterval(period)),
    enabled: cameraIds.length > 0,
    refetchInterval: 30000,
  });

  const { data: engagement } = useQuery({
    queryKey: ['analytics', 'engagement', cameraIds, period],
    queryFn: () => analyticsApi.getEngagement(cameraIds, period),
    enabled: cameraIds.length > 0,
    refetchInterval: 30000,
  });

  const { data: trafficPatterns } = useQuery({
    queryKey: ['analytics', 'traffic-patterns', cameraIds, period],
    queryFn: () => analyticsApi.getTrafficPatterns(cameraIds, period),
    enabled: cameraIds.length > 0,
    refetchInterval: 30000,
  });

  const { data: crossDemographics } = useQuery({
    queryKey: ['analytics', 'cross-demographics', cameraIds, period],
    queryFn: () => analyticsApi.getCrossDemographics(cameraIds, period),
    enabled: cameraIds.length > 0,
    refetchInterval: 30000,
  });

  const { data: videoStats } = useQuery({
    queryKey: ['analytics', 'video-stats', period],
    queryFn: () => analyticsApi.getVideoStats(period),
    refetchInterval: 30000,
  });

  // Build branch options for dropdown
  const branchOptions = [
    { value: 'all', label: 'All Branches' },
    ...(branches?.map((b) => ({ value: b.id, label: b.name })) || []),
    ...(cameras?.some((c) => !c.branch_id) ? [{ value: 'unassigned', label: 'Unassigned' }] : []),
  ];

  // Build camera options for dropdown (filtered by branch)
  const cameraOptions = [
    { value: 'all', label: 'All Cameras' },
    ...(branchFilteredCameras?.map((c) => ({ value: c.id, label: c.name })) || []),
  ];

  const genderData = summary
    ? [
        { name: 'Male', value: summary.gender_distribution.male },
        { name: 'Female', value: summary.gender_distribution.female },
      ]
    : [];

  const ageData = summary
    ? Object.entries(summary.age_distribution).map(([name, value]) => ({
        name,
        value: value as number,
      }))
    : [];

  const expressionData = summary
    ? Object.entries(summary.expression_distribution)
        .map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value: value as number,
        }))
        .sort((a, b) => b.value - a.value)
    : [];

  const formatAttentionTime = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = seconds / 60;
    return `${minutes.toFixed(1)}m`;
  };

  // Prepare engagement data
  const dwellHistogramData = engagement?.dwell_histogram.map((b) => ({
    name: b.range_label,
    value: b.count,
  })) || [];

  // Prepare traffic pattern data
  const dayOfWeekData = trafficPatterns?.day_of_week.map((d) => ({
    name: d.day_name,
    value: d.count,
  })) || [];

  // Prepare cross-demographic data
  const expressionByGenderData = crossDemographics?.expression_by_gender.map((r) => ({
    expression: r.expression.charAt(0).toUpperCase() + r.expression.slice(1),
    male_count: r.male_count,
    female_count: r.female_count,
  })) || [];

  const ageEngagementData = crossDemographics?.age_engagement.map((r) => ({
    name: r.age_group,
    value: Math.round(r.avg_attention_ms / 1000 * 10) / 10,
  })) || [];

  const expressionAttentionData = crossDemographics?.expression_attention.map((r) => ({
    name: r.expression.charAt(0).toUpperCase() + r.expression.slice(1),
    value: Math.round(r.avg_attention_ms / 1000 * 10) / 10,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Analytics Dashboard</h1>
          <p className="text-gray-500 dark:text-slate-500 mt-1">Monitor your retail traffic and demographics</p>
        </div>
        <div className="flex items-center gap-3">
          {branches && branches.length > 0 && (
            <Select
              options={branchOptions}
              value={selectedBranchId}
              onChange={(e) => {
                setSelectedBranchId(e.target.value);
                setSelectedCameraId('all');
              }}
              className="w-48"
            />
          )}
          <Select
            options={cameraOptions}
            value={selectedCameraId}
            onChange={(e) => setSelectedCameraId(e.target.value)}
            className="w-48"
          />
          <Select
            options={periodOptions}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-40"
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Viewers"
          value={summary?.total_detections.toLocaleString() || '0'}
          icon={<Users className="w-5 h-5" />}
        />
        <StatCard
          title="Unique Viewers"
          value={summary?.unique_viewers.toLocaleString() || '0'}
          icon={<Eye className="w-5 h-5" />}
        />
        <StatCard
          title="Avg. Attention"
          value={formatAttentionTime(summary?.avg_attention_ms || 0)}
          icon={<Clock className="w-5 h-5" />}
        />
        <StatCard
          title="Peak Hour"
          value={summary?.peak_hour !== undefined
            ? (() => {
                try {
                  const d = new Date();
                  d.setUTCHours(summary.peak_hour, 0, 0, 0);
                  return d.toLocaleTimeString(undefined, { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false });
                } catch { return `${summary.peak_hour}:00`; }
              })()
            : 'N/A'}
          icon={<TrendingUp className="w-5 h-5" />}
        />
      </div>

      {/* Traffic Chart */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-gray-400 dark:text-slate-500" />
          <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300">Viewer Traffic</h2>
        </div>
        {timeseries && timeseries.length > 0 ? (
          <AreaChart data={timeseries} height={350} timezone={timezone} />
        ) : (
          <div className="h-[350px] flex items-center justify-center text-gray-400 dark:text-slate-600">
            No data available for the selected period
          </div>
        )}
      </Card>

      {/* Demographics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <PieIcon className="w-4 h-4 text-gray-400 dark:text-slate-500" />
            <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300">Gender Distribution</h2>
          </div>
          {genderData.length > 0 ? (
            <PieChart data={genderData} colors={[colors.secondary, colors.pink]} height={250} />
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-slate-600">
              No data
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-gray-400 dark:text-slate-500" />
            <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300">Age Distribution</h2>
          </div>
          {ageData.length > 0 ? (
            <BarChart data={ageData} height={250} />
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-slate-600">
              No data
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4 text-gray-400 dark:text-slate-500" />
            <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300">Expressions</h2>
          </div>
          {expressionData.length > 0 ? (
            <BarChart data={expressionData.slice(0, 5)} color={colors.purple} height={250} />
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-slate-600">
              No data
            </div>
          )}
        </Card>
      </div>

      {/* Advert Performance */}
      {videoStats && videoStats.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Play className="w-4 h-4 text-gray-400 dark:text-slate-500" />
            <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">Advert Performance</h2>
          </div>
          <Card padding="none">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-slate-700">
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">Video</th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">Targeting</th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">Total Plays</th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">Unique Viewers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                {videoStats.map((v) => (
                  <tr key={v.video_id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Play className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]">{v.filename}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1.5">
                        {v.target_gender && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${v.target_gender === 'male' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'}`}>
                            {v.target_gender}
                          </span>
                        )}
                        {(v.target_age_min != null || v.target_age_max != null) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300">
                            {v.target_age_min ?? '?'}–{v.target_age_max ?? '?'}
                          </span>
                        )}
                        {!v.target_gender && v.target_age_min == null && v.target_age_max == null && (
                          <span className="text-xs text-gray-400 dark:text-slate-500">All</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{v.total_plays.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm text-gray-600 dark:text-slate-300">{v.unique_viewers.toLocaleString()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Camera Status */}
      {cameras && cameras.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-4 h-4 text-gray-400 dark:text-slate-500" />
            <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300">Camera Status</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cameras.map((camera) => (
              <div
                key={camera.id}
                className="flex items-center p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg"
              >
                <div
                  className={`w-3 h-3 rounded-full mr-3 ${
                    camera.is_online ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {camera.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {camera.is_online ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Engagement Depth */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-4 h-4 text-gray-400 dark:text-slate-500" />
          <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">Engagement Depth</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <StatCard
            title="Glancers (<2s)"
            value={engagement ? `${engagement.engagement_tiers.glancers.count.toLocaleString()} (${engagement.engagement_tiers.glancers.percentage.toFixed(1)}%)` : '0'}
            icon={<Eye className="w-5 h-5" />}
          />
          <StatCard
            title="Browsers (2-10s)"
            value={engagement ? `${engagement.engagement_tiers.browsers.count.toLocaleString()} (${engagement.engagement_tiers.browsers.percentage.toFixed(1)}%)` : '0'}
            icon={<Search className="w-5 h-5" />}
          />
          <StatCard
            title="Engaged (>10s)"
            value={engagement ? `${engagement.engagement_tiers.engaged.count.toLocaleString()} (${engagement.engagement_tiers.engaged.percentage.toFixed(1)}%)` : '0'}
            icon={<Target className="w-5 h-5" />}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <StatCard title="Min" value={engagement ? formatAttentionTime(engagement.dwell_distribution.min_ms) : '0'} icon={<Timer className="w-5 h-5" />} />
          <StatCard title="P25" value={engagement ? formatAttentionTime(engagement.dwell_distribution.p25_ms) : '0'} icon={<Timer className="w-5 h-5" />} />
          <StatCard title="Median" value={engagement ? formatAttentionTime(engagement.dwell_distribution.median_ms) : '0'} icon={<Timer className="w-5 h-5" />} />
          <StatCard title="P75" value={engagement ? formatAttentionTime(engagement.dwell_distribution.p75_ms) : '0'} icon={<Timer className="w-5 h-5" />} />
          <StatCard title="P90" value={engagement ? formatAttentionTime(engagement.dwell_distribution.p90_ms) : '0'} icon={<Timer className="w-5 h-5" />} />
          <StatCard title="Max" value={engagement ? formatAttentionTime(engagement.dwell_distribution.max_ms) : '0'} icon={<Timer className="w-5 h-5" />} />
        </div>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-gray-400 dark:text-slate-500" />
            <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300">Dwell Time Distribution</h2>
          </div>
          {dwellHistogramData.length > 0 ? (
            <BarChart data={dwellHistogramData} color={colors.purple} height={250} />
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-slate-600">
              No attention data available
            </div>
          )}
        </Card>
      </div>

      {/* Traffic Patterns */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-gray-400 dark:text-slate-500" />
          <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">Traffic Patterns</h2>
        </div>
        <div className={`grid grid-cols-1 ${dayOfWeekData.length > 0 ? 'lg:grid-cols-2' : ''} gap-6`}>
          <Card>
            <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300 mb-4">Hourly Traffic</h3>
            {trafficPatterns && trafficPatterns.hourly_breakdown.length > 0 ? (
              <BubbleHeatmapChart data={trafficPatterns.hourly_breakdown} height={160} timezone={timezone} />
            ) : (
              <div className="h-[160px] flex items-center justify-center text-gray-400 dark:text-slate-600">
                No data available
              </div>
            )}
          </Card>

          {dayOfWeekData.length > 0 && (
            <Card>
              <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300 mb-4">Day of Week</h3>
              <BarChart data={dayOfWeekData} color={colors.warning} height={120} />
            </Card>
          )}
        </div>
      </div>

      {/* Cross-Demographic Insights */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <GitBranch className="w-4 h-4 text-gray-400 dark:text-slate-500" />
          <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">Demographic Insights</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300 mb-4">Expression by Gender</h3>
            {expressionByGenderData.length > 0 ? (
              <GroupedBarChart
                data={expressionByGenderData}
                nameKey="expression"
                categories={[
                  { key: 'male_count', label: 'Male', color: colors.secondary },
                  { key: 'female_count', label: 'Female', color: colors.pink },
                ]}
                height={250}
              />
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-slate-600">
                No data
              </div>
            )}
          </Card>

          <Card>
            <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300 mb-4">Engagement by Age</h3>
            {ageEngagementData.length > 0 ? (
              <>
                <BarChart data={ageEngagementData} color={colors.success} height={220} />
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-2 text-center">Average attention time (seconds)</p>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-slate-600">
                No data
              </div>
            )}
          </Card>

          <Card>
            <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-slate-300 mb-4">Expression & Attention</h3>
            {expressionAttentionData.length > 0 ? (
              <>
                <BarChart data={expressionAttentionData} color={colors.pink} height={220} />
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-2 text-center">Average attention time (seconds)</p>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400 dark:text-slate-600">
                No data
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
